// User accounts and roles (administrators, SFCR_AUTH_MODE=local).
import { useState } from "react";
import { UserPlus } from "lucide-react";
import { api } from "../api/client";
import { invalidate, useMutation, useResource } from "../api/resource";
import type { CurrentUser } from "../api/types";
import { useCurrentUser } from "../auth";
import { ResetPasswordDialog } from "../components/password";
import { EmptyState, ErrorState, Notice, PageHeader, SkeletonBlock, StatusBadge } from "../components/ui";
import { w } from "../i18n";

type Role = CurrentUser["user"]["role"];
const ROLES: Role[] = ["preparer", "reviewer", "approver", "admin"];

export function UsersPage() {
  const { local, can, me } = useCurrentUser();
  const users = useResource(local && can("admin") ? "auth:users" : null, () => api.users());
  const create = useMutation(api.createUser);
  const update = useMutation(api.updateUser);
  const [form, setForm] = useState({ name: "", email: "", role: "preparer" as Role, password: "" });
  const [resetting, setResetting] = useState<{ id: string; name: string } | null>(null);
  const refresh = () => invalidate("auth:users");
  const submit = async () => {
    if (await create.run(form)) { setForm({ name: "", email: "", role: "preparer", password: "" }); refresh(); }
  };

  return <>
    <PageHeader eyebrow={w("ref.eyebrow")} title={w("users.title")} lead={w("users.lead")} />
    {!local && <Notice tone="neutral" title={w("users.offTitle")}>{w("users.offBody")}</Notice>}
    {local && !can("admin") && <Notice tone="caution">{w("users.adminOnly")}</Notice>}
    {local && can("admin") && <div className="ws-stack">
      {users.error && <ErrorState title={w("common.genericError")} error={users.error} onRetry={users.reload} />}
      {(create.error || update.error) && <ErrorState title={w("team.saveError")} error={create.error || update.error} />}
      <section className="ws-card">
        <div className="ws-card-head"><h2>{w("users.list")}</h2></div>
        {users.loading && <SkeletonBlock lines={4} />}
        {users.data && !users.data.items.length && <EmptyState title={w("users.empty")} />}
        {users.data && users.data.items.length > 0 && <div className="ws-table-wrap"><table className="ws-table">
          <thead><tr><th>{w("team.field.name")}</th><th>{w("team.field.email")}</th><th>{w("users.role")}</th><th>{w("team.status")}</th>
            <th><span className="ws-sr-only">{w("issues.col.action")}</span></th></tr></thead>
          <tbody>{users.data.items.map(user => {
            const self = user.email === me?.user.email;
            return <tr key={user.id}>
              <td><strong>{user.name}</strong></td><td className="ws-small">{user.email}</td>
              <td><select className="ws-select" aria-label={w("users.role")} value={user.role} disabled={self || update.pending}
                onChange={async e => { if (await update.run(user.id, { role: e.target.value as Role })) refresh(); }}>
                {ROLES.map(role => <option key={role} value={role}>{w(`role.user.${role}`)}</option>)}</select></td>
              <td><StatusBadge status={user.active ? "ready" : "not_started"} label={w(user.active ? "team.active" : "team.inactive")} /></td>
              <td style={{ textAlign: "right" }}>{!self && <span className="ws-row" style={{ justifyContent: "flex-end" }}>
                <button className="ws-btn sm ghost" onClick={() => setResetting({ id: user.id, name: user.name })}>{w("password.reset")}</button>
                <button className="ws-btn sm" disabled={update.pending}
                  onClick={async () => { if (await update.run(user.id, { active: !user.active })) refresh(); }}>
                  {w(user.active ? "team.deactivate" : "team.activate")}</button></span>}</td>
            </tr>;
          })}</tbody></table></div>}
        <div className="ws-card-pad">
          <div className="ws-team-form">
            {(["name", "email"] as const).map(key => <div key={key} className="ws-field" style={{ margin: 0 }}>
              <label htmlFor={`usr-${key}`}>{w(`team.field.${key}`)}</label>
              <input id={`usr-${key}`} className="ws-input" type={key === "email" ? "email" : "text"} value={form[key]}
                onChange={e => setForm({ ...form, [key]: e.target.value })} /></div>)}
            <div className="ws-field" style={{ margin: 0 }}><label htmlFor="usr-role">{w("users.role")}</label>
              <select id="usr-role" className="ws-select" value={form.role} onChange={e => setForm({ ...form, role: e.target.value as Role })}>
                {ROLES.map(role => <option key={role} value={role}>{w(`role.user.${role}`)}</option>)}</select></div>
            <div className="ws-field" style={{ margin: 0 }}><label htmlFor="usr-password">{w("users.password")}</label>
              <input id="usr-password" className="ws-input" type="password" autoComplete="new-password" value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })} /></div>
            <button className="ws-btn primary" disabled={create.pending || !form.name.trim() || !form.email.trim() || form.password.length < 12}
              onClick={submit}><UserPlus size={16} />{w("team.add")}</button>
          </div>
          <p className="ws-small ws-faint" style={{ marginBottom: 0 }}>{w("users.passwordHint")}</p>
        </div>
      </section>
      <Notice tone="neutral">{w("users.note")}</Notice>
      {resetting && <ResetPasswordDialog userId={resetting.id} name={resetting.name} onClose={() => setResetting(null)} />}
    </div>}
  </>;
}
